/**
 * @fileoverview Widget builder (MOR-906 §7.8)
 *
 * Compose a dashboard widget from building blocks — metric × group-by ×
 * period × boolean filter — and watch it run live (the backing query is
 * reactive, so a saved widget is never stale). Widgets save per user, star
 * onto the Dashboard board, and re-run with one click.
 *
 * Filtering is one mechanism: the shared boolean expression
 * (EntryQueryBuilder). Widgets saved with the older flat filters are folded
 * into editable conditions on load, so nothing filters invisibly.
 *
 * @component
 * @example
 * <WidgetBuilder orgId={orgId} pendingSpec={spec} onPendingConsumed={clear} />
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format } from "date-fns";
import { BookmarkPlus, Loader2, Play, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { validateTokens } from "@cvx/timesheets/query/entryQuery";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { formatAud, formatDateRange } from "./format";
import { EntryQueryBuilder } from "./EntryQueryBuilder";
import { ReportChart } from "./ReportChart";
import { useEntryQueryDimensions } from "./useEntryQueryDimensions";
import {
  DEFAULT_WIDGET_SPEC,
  foldLegacyFilters,
  type WidgetSpec,
} from "./widgetSpec";

// The usual card shadow reads against the muted page, but the builder opens
// on the dialog's white surface, where it disappears — hence the border.
const CARD_CLASS =
  "rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

const PRESETS = [
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_30", label: "Last 30 days" },
  { key: "last_90", label: "Last 90 days" },
  { key: "custom", label: "Custom range" },
] as const;

const GROUP_BYS = [
  { key: "category", label: "Category" },
  { key: "job", label: "Matter" },
  { key: "user", label: "Person" },
  { key: "date", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
] as const;

const METRICS = [
  { key: "hours", label: "Hours" },
  { key: "amount", label: "Approved value" },
  { key: "writeOff", label: "Written off" },
  { key: "entries", label: "Entry count" },
] as const;

export function WidgetBuilder({
  orgId,
  pendingSpec,
  pendingTitle,
  onPendingConsumed,
  pinOnSave,
}: {
  orgId: Id<"organizations">;
  /** A spec handed over from the AI ask box, adopted once then cleared. */
  pendingSpec?: WidgetSpec | null;
  pendingTitle?: string | null;
  onPendingConsumed?: () => void;
  /** Opened from the board, so Save is also the gesture that pins it there. */
  pinOnSave?: boolean;
}) {
  const [spec, setSpec] = useState<WidgetSpec>(DEFAULT_WIDGET_SPEC);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const today = format(new Date(), "yyyy-MM-dd");

  const patch = (p: Partial<WidgetSpec>) =>
    setSpec((prev) => ({ ...prev, ...p }));

  const { context, dimensions } = useEntryQueryDimensions(orgId, spec.scope);

  // An AI answer is a spec like any other — adopt it so it stays editable.
  useEffect(() => {
    if (!pendingSpec) return;
    setSpec(foldLegacyFilters(pendingSpec));
    if (pendingTitle) setSaveName(pendingTitle);
    onPendingConsumed?.();
  }, [pendingSpec, pendingTitle, onPendingConsumed]);

  // A half-built expression is a normal state mid-edit, not a failure — the
  // builder shows the reason inline, so hold the run rather than erroring.
  const filterError = validateTokens(spec.filter ?? []);

  const { data: result, error } = useQuery({
    ...convexQuery(api.timesheets.reportBuilder.runReport, {
      orgId,
      spec,
      today,
    }),
    enabled: !!context && !filterError,
  });

  const { data: saved } = useQuery(
    convexQuery(api.timesheets.reportBuilder.listSaved, { orgId }),
  );
  const saveReport = useMutation(api.timesheets.reportBuilder.saveReport);
  const deleteReport = useMutation(api.timesheets.reportBuilder.deleteReport);
  const setFavourite = useMutation(api.timesheets.reportBuilder.setFavourite);

  const canOrgScope = context?.canReportFirmWide ?? false;

  // Anyone who can see firm-wide time usually wants it, but whether they can
  // is only known once the context lands. Adjusted during render, not in an
  // effect: the context is normally already cached when the builder mounts,
  // so an effect would paint "My time" selected for a frame and visibly flip.
  // Identity against the default keeps this off an adopted or loaded spec.
  const [scopeDefaulted, setScopeDefaulted] = useState(false);
  if (canOrgScope && !scopeDefaulted) {
    setScopeDefaulted(true);
    setSpec((prev) =>
      prev === DEFAULT_WIDGET_SPEC ? { ...prev, scope: "org" } : prev,
    );
  }
  // The reverse case (MOR-1017): a saved Firm-scope report opened by someone
  // who cannot report firm-wide (a manager limited to their team). Rather
  // than refuse with a remedy the hidden scope control cannot perform, show
  // their own time and say so.
  const [narrowedFromFirm, setNarrowedFromFirm] = useState(false);
  if (context !== undefined && !canOrgScope && spec.scope === "org") {
    setNarrowedFromFirm(true);
    setSpec((prev) => ({ ...prev, scope: "me" }));
  }

  const handleSave = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      const name = saveName.trim();
      const reportId = await saveReport({ orgId, name, spec });
      if (pinOnSave) await setFavourite({ reportId, isFavourite: true });
      toast.success(
        pinOnSave ? `Saved "${name}" to your dashboard.` : `Saved "${name}".`,
      );
      setSaveOpen(false);
      setSaveName("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the widget."));
    } finally {
      setSaving(false);
    }
  };

  const isAmount = spec.metric === "amount" || spec.metric === "writeOff";
  const fmtValue = (value: number) =>
    isAmount
      ? formatAud(value)
      : value.toLocaleString("en-AU", { maximumFractionDigits: 1 });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {(saved ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Saved widgets:
          </span>
          {(saved ?? []).map((r) => (
            <span
              key={r._id}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background pl-2.5 text-xs"
            >
              <button
                type="button"
                className="py-1 hover:text-primary"
                onClick={() => {
                  setSpec(foldLegacyFilters(r.spec));
                  // Pre-fill the save name so tweaks update this widget in
                  // place — defaults are editable like anything else.
                  setSaveName(r.name);
                }}
                title="Load this widget — Save updates it in place"
              >
                {r.name}
              </button>
              <button
                type="button"
                className={cn(
                  "rounded-full p-1",
                  r.isFavourite
                    ? "text-amber-500"
                    : "text-muted-foreground hover:text-amber-500",
                )}
                onClick={() =>
                  setFavourite({
                    reportId: r._id,
                    isFavourite: !r.isFavourite,
                  }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not star the widget."),
                    ),
                  )
                }
                aria-label={
                  r.isFavourite
                    ? `Remove ${r.name} from the Dashboard`
                    : `Pin ${r.name} to the Dashboard`
                }
                title={
                  r.isFavourite
                    ? "Remove from the Dashboard board"
                    : "Pin to the Dashboard board"
                }
              >
                <Star
                  className={cn("size-3", r.isFavourite && "fill-current")}
                />
              </button>
              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteReport({ reportId: r._id }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not delete the widget."),
                    ),
                  )
                }
                aria-label={`Delete ${r.name}`}
              >
                <Trash2 className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Building blocks */}
      <div className={cn(CARD_CLASS, "flex flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Metric</Label>
            <Select
              value={spec.metric}
              onValueChange={(value) =>
                patch({ metric: value as WidgetSpec["metric"] })
              }
            >
              <SelectTrigger className="h-8 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRICS.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Grouped by</Label>
            <Select
              value={spec.groupBy}
              onValueChange={(value) =>
                patch({ groupBy: value as WidgetSpec["groupBy"] })
              }
            >
              <SelectTrigger className="h-8 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BYS.map((g) => (
                  <SelectItem key={g.key} value={g.key}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Split by</Label>
            <Select
              value={spec.seriesBy ?? "none"}
              onValueChange={(value) =>
                patch({
                  seriesBy:
                    value === "none"
                      ? undefined
                      : (value as NonNullable<WidgetSpec["seriesBy"]>),
                })
              }
            >
              <SelectTrigger className="h-8 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="user">Person</SelectItem>
                <SelectItem value="job">Matter</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Period</Label>
            <Select
              value={spec.preset}
              onValueChange={(value) => patch({ preset: value })}
            >
              <SelectTrigger className="h-8 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {spec.preset === "custom" && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">From</Label>
                <DatePicker
                  value={spec.startDate ?? ""}
                  onChange={(isoDate) =>
                    patch({ startDate: isoDate ?? undefined })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">To</Label>
                <DatePicker
                  value={spec.endDate ?? ""}
                  onChange={(isoDate) =>
                    patch({ endDate: isoDate ?? undefined })
                  }
                />
              </div>
            </>
          )}
          {narrowedFromFirm && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              This report was saved on Firm scope. Firm-wide reports are for
              approvers of everyone, so it is showing your own time.
            </p>
          )}
          {canOrgScope && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Scope</Label>
              <div className="flex items-center rounded-md border border-border">
                <Button
                  variant={spec.scope === "org" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 rounded-r-none"
                  onClick={() => patch({ scope: "org" })}
                >
                  Firm
                </Button>
                <Button
                  variant={spec.scope === "me" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 rounded-l-none"
                  onClick={() => patch({ scope: "me" })}
                >
                  My time
                </Button>
              </div>
            </div>
          )}

          <div className="ml-auto flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Display</Label>
              <Select
                value={spec.chart}
                onValueChange={(value) =>
                  patch({ chart: value as WidgetSpec["chart"] })
                }
              >
                <SelectTrigger className="h-8 w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar chart</SelectItem>
                  <SelectItem value="line">Line chart</SelectItem>
                  <SelectItem value="table">Table</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!!filterError}
              onClick={() => setSaveOpen(true)}
            >
              <BookmarkPlus className="size-4" />
              Save
            </Button>
          </div>
        </div>

        <div className="border-t border-border/50 pt-3">
          <EntryQueryBuilder
            tokens={spec.filter ?? []}
            onChange={(filter) =>
              patch({ filter: filter.length > 0 ? filter : undefined })
            }
            dimensions={dimensions}
          />
        </div>
      </div>

      {/* Result — live and reactive */}
      <div className={cn(CARD_CLASS, "min-w-0 p-4")}>
        {filterError ? (
          <p className="py-6 text-sm text-muted-foreground">
            Finish the filter above to run this widget.
          </p>
        ) : error ? (
          <p className="py-6 text-sm text-destructive">
            {extractErrorMessage(error, "Could not run the widget.")}
          </p>
        ) : result === undefined ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold">
                {METRICS.find((m) => m.key === spec.metric)?.label} by{" "}
                {GROUP_BYS.find(
                  (g) => g.key === spec.groupBy,
                )?.label.toLowerCase()}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDateRange(result.startDate, result.endDate)} · total{" "}
                {fmtValue(result.totalValue)}
                {result.truncated && " · large range truncated"}
              </p>
            </div>
            <ReportChart
              rows={result.rows}
              seriesNames={result.seriesNames}
              chart={spec.chart}
              metricLabel={result.metricLabel}
              groupLabel={
                GROUP_BYS.find((g) => g.key === spec.groupBy)?.label ?? "Group"
              }
              groupBy={spec.groupBy}
              categories={context?.categories}
            />
          </>
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this widget</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="widget-name">Name</Label>
            <Input
              id="widget-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "Billable hours by person, monthly"'
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!saveName.trim() || saving}
              onClick={() => void handleSave()}
            >
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Play className="size-4" />
              )}
              Save widget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
